import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { AppConfig } from "../../../app.config";
import { Audit, EmployeeProcess } from "../models";
import { firstValueFrom, Observable, of } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { FileDocument } from '../models/file-document.model';
import { ActionTypes, AuditParameters } from '../models/audit.model';
import { map } from 'rxjs/operators';
import { IPagedModel } from '../models/paged.model.';

@Injectable({
  providedIn: 'root'
})
export class AuditService {
  cppUrl = AppConfig.settings.apiUrls.cpp;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  create(audit: Audit): Observable<Audit> {
    return this.http.post<Audit>(`${this.cppUrl}/Audit`, audit);
  }
  auditSavePaycheck(process: EmployeeProcess, _autoView: boolean) {
    const entity = {
      _trec: process.type,
      _capellido: process.employeeLastName,
      _cnombre: process.employeeFirstName,
      _nroleg: process.employeeFile,
      _peri: process.periodValue,
      _state: process.stateAlias
    };

    firstValueFrom(this.create({
      id: null,
      entity: JSON.stringify(entity),
      organizationalUnitId: this.authService.getOrganizationId(),
      organizationalUnitName: this.authService.getOrganizationName(),
      userId: Number(this.authService.getUserId()),
      userName: this.authService.getUserNick(),
      applicationId: AppConfig.settings.application.id,
      applicationName: AppConfig.settings.application.code,
      creationDate: new Date(),
      actionType: { id: 58 },
      entityName: null,
      entityid: 0,
      keys: [
        { Key: "USER", Value: Number(this.authService.getUserId()) },
        { Key: "PROCESS", Value: process.id }]
    }));
  }

  auditPrintPaycheck(process: EmployeeProcess, _autoView: boolean) {
    const entity = {
      _trec: process.type,
      _capellido: process.employeeLastName,
      _cnombre: process.employeeFirstName,
      _nroleg: process.employeeFile,
      _peri: process.periodValue,
      _state: process.stateAlias
    };

    firstValueFrom(this.create({
      id: null,
      entity: JSON.stringify(entity),
      organizationalUnitId: this.authService.getOrganizationId(),
      organizationalUnitName: this.authService.getOrganizationName(),
      userId: Number(this.authService.getUserId()),
      userName: this.authService.getUserNick(),
      applicationId: AppConfig.settings.application.id,
      applicationName: AppConfig.settings.application.code,
      creationDate: new Date(),
      actionType: { id: 60 },
      entityName: null,
      entityid: 0,
      keys: [
        { Key: "USER", Value: Number(this.authService.getUserId()) },
        { Key: "PROCESS", Value: process.id }]
    }));
  }

  auditSaveEmployeeDocument(doc: FileDocument, autoView: boolean) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const auditKeys: (any) = [{ Key: "USER", Value: Number(this.authService.getUserId()) }, { Key: "FILEDOCUMENT", Value: doc.id }];
    const entityEmployeeDocument = this.getAuditEntityForEmployeeDocument(doc);
    const actionTypeId = autoView ? 73 : 70;

    firstValueFrom(this.create({
      id: null,
      entity: JSON.stringify(entityEmployeeDocument),
      organizationalUnitId: this.authService.getOrganizationId(),
      organizationalUnitName: this.authService.getOrganizationName(),
      userId: Number(this.authService.getUserId()),
      userName: this.authService.getUserNick(),
      applicationId: AppConfig.settings.application.id,
      applicationName: AppConfig.settings.application.code,
      creationDate: new Date(),
      actionType: { id: actionTypeId },
      entityName: null,
      entityid: 0,
      keys: auditKeys
    }));
  }

  private getAuditEntityForEmployeeDocument(doc: FileDocument) {
    return {
      _docId: doc.id,
      _capellido: doc.employeeLastName,
      _cnombre: doc.employeeFirstName,
      _nroleg: doc.nroLegajo,
      _fecdoc: doc.documentDate
    };
  }

  auditPrintEmployeeDocument(doc: FileDocument, autoView: boolean) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const auditKeys: (any) = [{ Key: "USER", Value: Number(this.authService.getUserId()) }, { Key: "FILEDOCUMENT", Value: doc.id }];
    const entityEmployeeDocument = this.getAuditEntityForEmployeeDocument(doc);
    const actionTypeId = autoView ? 74 : 71;

    firstValueFrom(this.create({
      id: null,
      entity: JSON.stringify(entityEmployeeDocument),
      organizationalUnitId: this.authService.getOrganizationId(),
      organizationalUnitName: this.authService.getOrganizationName(),
      userId: Number(this.authService.getUserId()),
      userName: this.authService.getUserNick(),
      applicationId: AppConfig.settings.application.id,
      applicationName: AppConfig.settings.application.code,
      creationDate: new Date(),
      actionType: { id: actionTypeId },
      entityName: null,
      entityid: 0,
      keys: auditKeys
    }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(parameters: any): Observable<IPagedModel<any>> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cleanParams: any = {
      ...parameters,
      organizationalUnitId: this.resolveOrganizationalUnitId(parameters?.organizationalUnitId)
    };

    return this.http.get<IPagedModel<any>>(`${this.cppUrl}/Audit/GetAudits`, { params: cleanParams });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getActionTypes(): Observable<any> {
    const actionTypes: ActionTypes = JSON.parse(localStorage.getItem(`actionTypesAudit`));

    if (actionTypes) {
      return of(actionTypes);
    }

    return this.http.get<ActionTypes[]>(`${this.cppUrl}/Audit/GetActionTypes/`).pipe(
      map(res => {
        localStorage.setItem(`actionTypesAudit`, JSON.stringify(res));
        return res;
      })
    );
  }

  export(parameters: AuditParameters) {
    const body: AuditParameters = {
      ...parameters,
      organizationalUnitId: this.resolveOrganizationalUnitId(parameters?.organizationalUnitId)
    };

    return this.http.put<unknown>(
      `${this.cppUrl}/Audit/ExportAudits`,
      body
    );
  }

  private resolveOrganizationalUnitId(organizationalUnitId?: number): number {
    return organizationalUnitId && Number(organizationalUnitId) > 0
      ? Number(organizationalUnitId)
      : Number(this.authService.getOrganizationId());
  }
}